/**
 * Light or dark, per person and per DEVICE.
 *
 * Per device is a deliberate exception to how every other preference in this
 * app is stored. `webledgerPrefs` is server-synced and says why: "which
 * currency did I last use is a fact about a person, not about a browser
 * profile". That reasoning is exactly what puts the theme on the other side
 * of the line — whether a screen should be dark is a fact about the room and
 * the display in front of you, not about who you are. A laptop in a bright
 * office and a phone at night want different answers from the same person on
 * the same evening.
 *
 * Keyed by user as well as device, because a shared machine has more than one
 * person on it and one of them turning the lights off should not do it for
 * everybody.
 *
 * The default is `system`, so most people never open the control: the OS
 * already knows, and it already changes at dusk if they asked it to.
 *
 * Applied by setting `data-bs-theme` on <html> — Bootstrap 5.3's own
 * attribute, which themes the framework's components, and which
 * `packages/ui-web`'s token block hangs its dark values off. One attribute,
 * both halves of the page.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = (userId: string): string => `colplat:theme:${userId}`;

function media(): MediaQueryList | undefined {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : undefined;
}

export function getTheme(userId: string): ThemeChoice {
  try {
    const raw = localStorage.getItem(KEY(userId));
    return raw === 'light' || raw === 'dark' ? raw : 'system';
  } catch {
    // Private windows and blocked site data both throw here. The OS setting
    // is a perfectly good answer when we cannot remember a choice.
    return 'system';
  }
}

/** What `system` currently resolves to. */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return media()?.matches ? 'dark' : 'light';
}

export function applyTheme(userId: string | undefined): void {
  const choice = userId ? getTheme(userId) : 'system';
  document.documentElement.setAttribute('data-bs-theme', resolveTheme(choice));
}

export function setTheme(userId: string, choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(KEY(userId));
    else localStorage.setItem(KEY(userId), choice);
  } catch {
    // Unstorable is not unusable: the choice still applies for this session.
  }
  document.documentElement.setAttribute('data-bs-theme', resolveTheme(choice));
}

/**
 * Follow the OS while the choice is `system`, without a reload.
 *
 * Someone who has their Mac switch at sunset expects the page in front of
 * them to switch with it, not at the next navigation.
 */
export function watchSystemTheme(userId: string | undefined): void {
  const mq = media();
  if (!mq) return;
  mq.addEventListener('change', () => {
    if (!userId || getTheme(userId) === 'system') applyTheme(userId);
  });
}
